import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { User } from '../models/User';
import { Establishment } from '../models/Establishment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userSource: BehaviorSubject<any> = new BehaviorSubject<User | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private cookie: CookieService
  ) {
    this.setUser(this.user);
  }

  setUser(value: User | null) {
    this.userSource.next(value);
  }

  getUser(): Observable<User> {
    return this.userSource.asObservable();
  }

  get token(): string {
    try {
      return this.cookie.get('token');
    } catch (error) {
      return '';
    }
  }

  /** Unix timestamp (seconds) when the JWT expires, stored at login. */
  get tokenExpiry(): number | null {
    const raw = localStorage.getItem('token_expiry');
    const expiry = raw ? Number(raw) : NaN;
    return Number.isFinite(expiry) ? expiry : null;
  }

  /** True when there is no token, or the stored expiry is in the past. */
  isTokenExpired(): boolean {
    if (!this.token) {
      return true;
    }
    const expiry = this.tokenExpiry;
    return expiry !== null && Date.now() / 1000 >= expiry;
  }

  get user(): User | null {
    try {
      return JSON.parse(localStorage.getItem('user')!);
    } catch {
      return null;
    }
  }
  get establishment(): Establishment | null {
    try {
      // @ts-ignore
      return JSON.parse(localStorage.getItem('temp_establishment'));
    } catch {
      return null;
    }
  }

  get id(): number | null {
    try {
      return Number(localStorage.getItem('temp_id'));
    } catch (error) {
      return null;
    }
  }
  notif() {
    return this.http.get(`${environment.api.BASE_URL}/agencies/notifications`);
  }
  readNotif(id: number, body: any) {
    return this.http.put(
      `${environment.api.BASE_URL}/notifications/${id}/read`,
      body
    );
  }
  login(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/sign_in`, body);
  }
  fogetPassword(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/password/forgot`, body);
  }

  validateSiret(siret: string): Observable<any> {
    return this.http.get(`${environment.api.SIRET_API_URL}/${siret}`);
  }

  signupAgency(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/sign_up`, body);
  }

  // One-time agency onboarding for a fresh instance (creates the agency + its
  // AGENCY / AGENCY_USER accounts). Public endpoint.
  onboard(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/onboard`, body);
  }

  // Public: whether the instance already has an agency (AGENCY user) registered.
  // Used to hide the registration CTA and block the /agency/register route.
  onboardingStatus(): Observable<any> {
    return this.http.get(`${environment.api.BASE_URL}/auth/onboarding-status`);
  }

  signup(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}`, body);
  }

  activate(email: string, code: string): Observable<any> {
    return this.http.get(`${environment.api.BASE_URL}/auth/check_verification_code?email=${email}&code=${code}`);
  }

  disconnect() {
    localStorage.clear();
    this.cookie.delete('token');
    this.cookie.delete('token', '/');
    this.setUser(null);
    this.router.navigate(['/login']);
  }
}
